import React from 'react'
import Icon from '@mdi/react'
import {
  mdiMenu,
  mdiRuler,
  mdiAngleAcute,
  mdiAngleRight,
  mdiEyedropper,
  mdiWhiteBalanceSunny,
  mdiViewGridPlusOutline,
  mdiFullscreen,
  mdiFullscreenExit,
  mdiChevronLeft,
  mdiChevronRight,
  mdiTools,
  mdiHospitalBox,
  mdiMagnifyPlusOutline,
  mdiCursorDefault,
  mdiArrowSplitHorizontal,
  mdiEyeOff,
  mdiVectorLink,
  mdiViewColumn,
  mdiAlphaCBox,
  mdiAlphaSBox,
  mdiAlphaABox,
  mdiRefresh,
  mdiCrosshairsGps,
  mdiDownload,
  mdiCamera,
} from '@mdi/js'
import { portalSectionVisible } from '../../plan/portalChrome'
import {
  studyLoadSourceChipLabel,
  studyLoadSourceDownloadTitle,
} from '../../utils/studyLoadSource'
import OviyamNgFlyoutTool from './OviyamNgFlyoutTool'

/** Tool icon-only (estilo Oviyam 2.x / Mayam). */
function Tool({ title, path, active, activeAlt, onClick, disabled }) {
  const cls = [
    'udv-oviyamng-tool',
    active ? 'is-active' : '',
    activeAlt ? 'is-active-alt' : '',
  ]
    .filter(Boolean)
    .join(' ')
  return (
    <button
      type="button"
      className={cls}
      title={title}
      aria-label={title}
      onClick={onClick}
      disabled={disabled}
    >
      <Icon path={path} size={0.78} color="currentColor" />
    </button>
  )
}

const DEFAULT_IDS = {
  nav: 'notool',
  mpr: 'orthogonal',
  imagen: 'Wwwc',
  medir: 'Length',
  estudio: 'zip',
}

/**
 * Header OviyamNGNG: chrome izq/der + flyouts centrados (Nav / MPR / Imagen / Medir / Estudio).
 * Rail derecho intacto — este header es acceso rápido duplicado.
 */
function OviyamNgHeader({
  planLabel,
  toolActive,
  isFullscreen,
  showMenu = true,
  anyOpen = false,
  sidebarCollapsed = false,
  toolsCollapsed = false,
  hideToolsRail = false,
  onToggleMenu,
  onToggleSidebar,
  onToggleToolsRail,
  onLayout,
  onToggleFullscreen,
  toolExecute,
  onPresets,
  showPresets = true,
  onCaptureViewport,
  portalChrome = null,
  bell,
  loadIndicator = null,
  studyLoadSource = null,
  toolsProps = null,
}) {
  const [defaultIds, setDefaultIds] = React.useState(DEFAULT_IDS)
  const showSec = (id) => portalSectionVisible(portalChrome, id)
  const run = (tool) => toolExecute && toolExecute(tool)
  const tp = toolsProps || {}

  const setDefault = (group, id) => {
    setDefaultIds((prev) => (prev[group] === id ? prev : { ...prev, [group]: id }))
  }

  const pickDefault = (group, items) => {
    if (!items.length) return null
    const want = defaultIds[group]
    return items.find((i) => i.id === want) || items[0]
  }

  const sourceLabel = studyLoadSource
    ? studyLoadSourceChipLabel(studyLoadSource)
    : null

  // —— Nav ——
  const navItems = []
  if (showSec('nav')) {
    navItems.push({
      id: 'notool',
      label: 'No Tool',
      path: mdiCursorDefault,
      active: toolActive === 'notool',
      onClick: () => {
        setDefault('nav', 'notool')
        run('notool')
      },
    })
    if (tp.showReferenceLines !== false) {
      navItems.push({
        id: 'referencelines',
        label: 'Reference Lines',
        path: mdiArrowSplitHorizontal,
        active: !!tp.referenceLines,
        disabled: !anyOpen,
        onClick: () => {
          setDefault('nav', 'referencelines')
          run('referencelines')
        },
      })
      if (tp.referenceLines) {
        navItems.push({
          id: 'referencelinesactivehide',
          label: 'Ocultar línea en activo',
          path: mdiEyeOff,
          active: !!tp.hideReferenceLinesOnActive,
          disabled: !anyOpen,
          onClick: () => {
            setDefault('nav', 'referencelinesactivehide')
            run('referencelinesactivehide')
          },
        })
      }
    }
    navItems.push({
      id: 'serieslink',
      label: 'Link Series',
      path: mdiVectorLink,
      active: !!tp.seriesLink,
      disabled: !!tp.mprActive,
      onClick: () => {
        setDefault('nav', 'serieslink')
        run('serieslink')
      },
    })
    if (tp.seriesLink || tp.mprActive) {
      navItems.push({
        id: 'serieslinkwwwc',
        label: tp.mprActive ? 'Link WW/WL (MPR)' : 'Link WW/WL',
        path: mdiWhiteBalanceSunny,
        active: !!tp.seriesLinkWwwc,
        onClick: () => {
          setDefault('nav', 'serieslinkwwwc')
          run('serieslinkwwwc')
        },
      })
    }
  }
  const navDefault = pickDefault('nav', navItems)
  const navActive = navItems.some((i) => i.active)

  // —— MPR ——
  const mprItems = []
  if (anyOpen && tp.onMpr && showSec('mpr')) {
    if (tp.mprActive && tp.onMprExit) {
      mprItems.push({
        id: 'mpr-exit',
        label: 'Salir de MPR',
        path: mdiRefresh,
        onClick: () => {
          setDefault('mpr', 'mpr-exit')
          tp.onMprExit()
        },
      })
    }
    if (tp.mprOrthogonalAllowed) {
      mprItems.push({
        id: 'orthogonal',
        label: 'MPR Orthogonal',
        path: mdiViewColumn,
        active: !!tp.visibleMprOrthogonal,
        disabled: !tp.mprAllowed,
        onClick: () => {
          setDefault('mpr', 'orthogonal')
          tp.onMpr('orthogonal')
        },
      })
    }
    mprItems.push(
      {
        id: 'coronal',
        label: 'MPR Coronal',
        path: mdiAlphaCBox,
        active: !!tp.visibleMprCoronal,
        disabled: !tp.mprAllowed,
        onClick: () => {
          setDefault('mpr', 'coronal')
          tp.onMpr('coronal')
        },
      },
      {
        id: 'sagittal',
        label: 'MPR Sagittal',
        path: mdiAlphaSBox,
        active: !!tp.visibleMprSagittal,
        disabled: !tp.mprAllowed,
        onClick: () => {
          setDefault('mpr', 'sagittal')
          tp.onMpr('sagittal')
        },
      },
      {
        id: 'axial',
        label: 'MPR Axial',
        path: mdiAlphaABox,
        active: !!tp.visibleMprAxial,
        disabled: !tp.mprAllowed,
        onClick: () => {
          setDefault('mpr', 'axial')
          tp.onMpr('axial')
        },
      }
    )
    if (tp.mprActive && tp.onMprCrosshairToggle) {
      mprItems.push({
        id: 'crosshair',
        label: 'Punto mira',
        path: mdiCrosshairsGps,
        active: !!tp.mprCrosshairActive,
        onClick: () => {
          setDefault('mpr', 'crosshair')
          tp.onMprCrosshairToggle()
        },
      })
    }
  }
  const mprDefault = pickDefault('mpr', mprItems)
  const mprGroupActive =
    !!tp.mprActive ||
    !!tp.visibleMprOrthogonal ||
    !!tp.visibleMprCoronal ||
    !!tp.visibleMprSagittal ||
    !!tp.visibleMprAxial

  // —— Imagen ——
  const imagenItems = []
  if (showSec('imagen')) {
    imagenItems.push({
      id: 'Wwwc',
      label: 'WW/WC',
      path: mdiWhiteBalanceSunny,
      active: toolActive === 'Wwwc',
      disabled: !anyOpen,
      onClick: () => {
        setDefault('imagen', 'Wwwc')
        run('Wwwc')
      },
    })
    if (showPresets && onPresets) {
      imagenItems.push({
        id: 'presets',
        label: 'Presets',
        path: mdiHospitalBox,
        disabled: !anyOpen,
        onClick: () => {
          setDefault('imagen', 'presets')
          onPresets()
        },
      })
    }
    imagenItems.push(
      {
        id: 'Probe',
        label: 'Probe',
        path: mdiEyedropper,
        active: toolActive === 'Probe',
        disabled: !anyOpen,
        onClick: () => {
          setDefault('imagen', 'Probe')
          run('Probe')
        },
      },
      {
        id: 'Magnify',
        label: 'Lupa',
        path: mdiMagnifyPlusOutline,
        active: toolActive === 'Magnify',
        disabled: !anyOpen,
        onClick: () => {
          setDefault('imagen', 'Magnify')
          run('Magnify')
        },
      }
    )
  }
  const imagenDefault = pickDefault('imagen', imagenItems)
  const imagenActive =
    toolActive === 'Wwwc' ||
    toolActive === 'Probe' ||
    toolActive === 'Magnify'

  // —— Medir ——
  const medirItems = []
  if (showSec('medir')) {
    medirItems.push(
      {
        id: 'Length',
        label: 'Regla',
        path: mdiRuler,
        active: toolActive === 'Length',
        disabled: !anyOpen,
        onClick: () => {
          setDefault('medir', 'Length')
          run('Length')
        },
      },
      {
        id: 'Angle',
        label: 'Ángulo',
        path: mdiAngleAcute,
        active: toolActive === 'Angle',
        disabled: !anyOpen,
        onClick: () => {
          setDefault('medir', 'Angle')
          run('Angle')
        },
      },
      {
        id: 'CobbAngle',
        label: 'Cobb',
        path: mdiAngleRight,
        active: toolActive === 'CobbAngle',
        disabled: !anyOpen,
        onClick: () => {
          setDefault('medir', 'CobbAngle')
          run('CobbAngle')
        },
      }
    )
  }
  const medirDefault = pickDefault('medir', medirItems)
  const medirActive =
    toolActive === 'Length' ||
    toolActive === 'Angle' ||
    toolActive === 'CobbAngle'

  // —— Estudio (descargas) ——
  const estudioItems = []
  if (showSec('estudio')) {
    const zipOk =
      !!(tp.studyDownloadUrl || tp.localStudyZipReady) && !tp.studyZipDownloadBusy
    const zipEligible =
      !!(tp.studyDownloadUrl || tp.localStudyZipEligible) && !!tp.onDownloadStudy
    if (zipEligible) {
      estudioItems.push({
        id: 'zip',
        label: 'Descargar ZIP / DICOM',
        path: mdiDownload,
        disabled: !zipOk || !anyOpen,
        onClick: () => {
          setDefault('estudio', 'zip')
          tp.onDownloadStudy()
        },
      })
    }
    if (onCaptureViewport || tp.onCaptureViewport) {
      const capture = onCaptureViewport || tp.onCaptureViewport
      estudioItems.push({
        id: 'jpg',
        label: 'Capturar JPG/PNG',
        path: mdiCamera,
        disabled: !anyOpen,
        onClick: () => {
          setDefault('estudio', 'jpg')
          capture()
        },
      })
    }
  }
  const estudioDefault = pickDefault('estudio', estudioItems)

  const flyouts = [
    navDefault
      ? {
          key: 'nav',
          title: 'Nav',
          path: navDefault.path,
          active: navActive,
          disabled: false,
          items: navItems,
          onPrimary: () => navDefault.onClick(),
        }
      : null,
    mprDefault
      ? {
          key: 'mpr',
          title: 'MPR',
          path: mprDefault.path,
          active: mprGroupActive,
          disabled: !tp.mprAllowed && !tp.mprActive,
          items: mprItems,
          onPrimary: () => mprDefault.onClick(),
        }
      : null,
    imagenDefault
      ? {
          key: 'imagen',
          title: 'Imagen',
          path: imagenDefault.path,
          active: imagenActive,
          disabled: !anyOpen,
          items: imagenItems,
          onPrimary: () => imagenDefault.onClick(),
        }
      : null,
    medirDefault
      ? {
          key: 'medir',
          title: 'Medir',
          path: medirDefault.path,
          active: medirActive,
          disabled: !anyOpen,
          items: medirItems,
          onPrimary: () => medirDefault.onClick(),
        }
      : null,
    estudioDefault
      ? {
          key: 'estudio',
          title: 'Estudio',
          path: estudioDefault.path,
          active: false,
          disabled: !anyOpen,
          items: estudioItems,
          onPrimary: () => estudioDefault.onClick(),
        }
      : null,
  ].filter(Boolean)

  return (
    <header className="udv-oviyamng-header">
      <div className="udv-oviyamng-header-left">
        <div className="udv-oviyamng-header-brand">
          {showMenu ? (
            <Tool title="Menú" path={mdiMenu} onClick={onToggleMenu} />
          ) : null}
          {onToggleSidebar ? (
            <Tool
              title={sidebarCollapsed ? 'Mostrar series' : 'Ocultar series'}
              path={sidebarCollapsed ? mdiChevronRight : mdiChevronLeft}
              active={!sidebarCollapsed}
              onClick={onToggleSidebar}
            />
          ) : null}
          {!hideToolsRail && onToggleToolsRail ? (
            <Tool
              title={
                toolsCollapsed ? 'Mostrar herramientas' : 'Ocultar herramientas'
              }
              path={mdiTools}
              active={!toolsCollapsed}
              onClick={onToggleToolsRail}
            />
          ) : null}
          <span className="udv-oviyamng-brand-mark">UDV</span>
          {planLabel ? (
            <span className="udv-oviyamng-brand-mode">{planLabel}</span>
          ) : null}
        </div>
        {loadIndicator || sourceLabel ? (
          <div className="udv-oviyamng-source-slot" aria-label="Origen del estudio">
            {loadIndicator || (
              <span
                className="udv-oviyamng-source-chip"
                title={studyLoadSourceDownloadTitle(studyLoadSource)}
              >
                {sourceLabel}
              </span>
            )}
          </div>
        ) : null}
      </div>

      <div className="udv-oviyamng-header-center" aria-label="Herramientas rápidas">
        <Tool title="Layout" path={mdiViewGridPlusOutline} onClick={onLayout} />
        {flyouts.map((f) => (
          <OviyamNgFlyoutTool
            key={f.key}
            title={f.title}
            path={f.path}
            active={f.active}
            disabled={f.disabled}
            items={f.items}
            onPrimary={f.onPrimary}
          />
        ))}
      </div>

      <div className="udv-oviyamng-header-right">
        <Tool
          title={isFullscreen ? 'Salir fullscreen' : 'Fullscreen'}
          path={isFullscreen ? mdiFullscreenExit : mdiFullscreen}
          onClick={onToggleFullscreen}
        />
        {bell}
      </div>
    </header>
  )
}

export default OviyamNgHeader
